import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import CircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import { Button } from 'Components/ComponentLibrary/ExternalLibraries';
import { noop } from 'Helpers/PropTypes';

export const SIZES = ['lg', 'md', 'sm', 'xs'];

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.radius.full,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightFacetBold,
  },
  buttonLoader: {
    color: theme.palette.common.white,
  },
}));

const RoundedButton = React.forwardRef(({
  className,
  children,
  color,
  size,
  fullWidth,
  test,
  disabled,
  onClick,
  isLoading,
  padding,
  component,
  startIcon,
  endIcon,
  to,
  variant,
  openInNewTab,
  'data-test': dataTest,
}, ref) => {
  const classes = useStyles();

  const extraProps = {};
  // Exclude the following props if they are not included
  if (to) {
    extraProps.href = to;
    if (openInNewTab) {
      extraProps.target = '_blank';
      extraProps.rel = 'noopener noreferrer';
    }
  }
  if (component) {
    extraProps.component = component;
  }

  return (
    <Button
      data-test={dataTest}
      ref={ref}
      className={classNames(classes.root, className)}
      disableElevation
      variant={variant}
      padding={padding}
      color={color}
      size={size}
      fullWidth={fullWidth}
      test={test}
      disabled={disabled}
      onClick={onClick}
      startIcon={(isLoading) ? (
        <CircularProgress
          test="loading-indicator"
          className={classes.buttonLoader}
          size={20}
        />) : startIcon
      }
      endIcon={endIcon}
      {...extraProps}
    >
      {children}
    </Button>
  );
});

RoundedButton.defaultProps = {
  className: '',
  color: 'primary',
  isLoading: false,
  size: 'md',
  fullWidth: false,
  disabled: false,
  onClick: noop,
  padding: 'thick',
  test: null,
  component: null,
  startIcon: null,
  endIcon: null,
  to: null,
  variant: 'contained',
  openInNewTab: false,
  'data-test': null,
};

RoundedButton.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  startIcon: PropTypes.object,
  endIcon: PropTypes.object,
  color: PropTypes.string,
  onClick: PropTypes.func,
  isLoading: PropTypes.bool,
  size: PropTypes.oneOf(SIZES),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  padding: PropTypes.string,
  test: PropTypes.string,
  component: PropTypes.object,
  to: PropTypes.string,
  variant: PropTypes.oneOf(['contained', 'text', 'outlined']),
  openInNewTab: PropTypes.bool,
  'data-test': PropTypes.string,
};

export default RoundedButton;
